import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Search, MapPin, Copy, Check, Navigation, Crosshair, Target, Radio, Siren,
  User, Clock, Loader2, RefreshCw,
} from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, useMap, LayersControl } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import api, { formatApiError } from '../lib/api';
import { toast } from 'sonner';
import { useLiveEvents } from '../lib/events';

// Default Leaflet icon fix
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const SA_DEFAULT = [-26.107567, 28.056702]; // Sandton
const DEFAULT_ZOOM = 10;

const STATUS_COLOR = {
  open: '#ef4444',
  in_progress: '#f97316',
  closed: '#16a34a',
  cancelled: '#94a3b8',
};

const STATUS_LABEL = {
  open: 'Open', in_progress: 'In Progress', closed: 'Closed', cancelled: 'Cancelled',
};

import { computeSla, fmtSlaShort, SLA_TONE_META } from '../lib/sla';

// Build a colored DivIcon (avoids needing image assets per status).
// slaTone overrides colour: 'red' / 'breach' → red marker with fast pulse halo.
const incidentIcon = (status, pulse = false, slaTone = null) => {
  const slaCritical = slaTone === 'red' || slaTone === 'breach';
  const fill = slaCritical ? '#dc2626' : (STATUS_COLOR[status] || '#64748b');
  const haloAnim = slaTone === 'breach' ? 'csgPing 0.9s ease-out infinite'
                 : slaTone === 'red'    ? 'csgPing 1.2s ease-out infinite'
                 : 'csgPing 1.4s ease-out infinite';
  const showHalo = pulse || slaCritical;
  return L.divIcon({
    className: 'csg-incident-marker',
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    html: `
      <div style="position:relative;width:32px;height:32px;">
        ${showHalo ? `<span style="position:absolute;inset:0;background:${fill}55;border-radius:9999px;animation:${haloAnim};"></span>` : ''}
        ${slaCritical ? `<span style="position:absolute;inset:-4px;border:2px solid ${fill};border-radius:9999px;animation:${haloAnim};opacity:0.7;"></span>` : ''}
        <div style="position:absolute;inset:5px;background:${fill};border:2px solid white;border-radius:9999px;box-shadow:0 2px 6px rgba(0,0,0,.2);display:flex;align-items:center;justify-content:center;">
          <span style="color:white;font-size:10px;font-weight:700;">${slaCritical ? '!' : '!'}</span>
        </div>
      </div>`,
  });
};

const contractorIcon = (initials) => L.divIcon({
  className: 'csg-contractor-marker',
  iconSize: [28, 28],
  iconAnchor: [14, 14],
  html: `
    <div style="position:relative;width:28px;height:28px;background:#0D5B69;border:2px solid white;border-radius:9999px;box-shadow:0 2px 6px rgba(0,0,0,.2);display:flex;align-items:center;justify-content:center;">
      <span style="color:white;font-size:9px;font-weight:700;letter-spacing:0.4px;">${initials}</span>
    </div>`,
});


// CSS keyframes (injected once on mount)
const CSS_TAG_ID = 'csg-mapview-css';
const injectMapCss = () => {
  if (document.getElementById(CSS_TAG_ID)) return;
  const s = document.createElement('style');
  s.id = CSS_TAG_ID;
  s.innerHTML = `@keyframes csgPing { 0%{transform:scale(.6);opacity:1;} 100%{transform:scale(1.8);opacity:0;} }`;
  document.head.appendChild(s);
};


const Recenter = ({ lat, lng, zoom = 14 }) => {
  const map = useMap();
  useEffect(() => {
    if (lat != null && lng != null) map.flyTo([lat, lng], zoom, { duration: 0.6 });
  }, [lat, lng, zoom, map]);
  return null;
};


export const MapView = () => {
  const navigate = useNavigate();
  const [incidents, setIncidents] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [services, setServices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [live, setLive] = useState(false);

  // Location lookup state
  const [lookupAddress, setLookupAddress] = useState('');
  const [lookupCoords, setLookupCoords] = useState(null); // {lat,lng}
  const [lookupBusy, setLookupBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  // Tick every 30s so the SLA badges + pin colours stay live even without backend events
  const [, slaTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => slaTick((n) => n + 1), 30000);
    return () => clearInterval(id);
  }, []);
  const debounceRef = useRef();

  useEffect(injectMapCss, []);

  const load = useCallback(async () => {
    try {
      const [i, c, s, k] = await Promise.all([
        api.get('/incidents'),
        api.get('/contractors'),
        api.get('/services'),
        api.get('/customers'),
      ]);
      setIncidents(i.data || []);
      setContractors(c.data || []);
      setServices(s.data || []);
      setCustomers(k.data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useLiveEvents(
    ['incident.created', 'incident.updated', 'incident.closed',
      'incident.cancelled', 'incident.reopened',
      'incident.assignment_added', 'incident.assignment_ended', 'connected'],
    (event) => {
      if (event.type === 'connected') { setLive(true); return; }
      load();
    },
  );

  const serviceById = useMemo(() => Object.fromEntries(services.map((s) => [s.id, s])), [services]);
  const customerById = useMemo(() => Object.fromEntries(customers.map((c) => [c.id, c])), [customers]);
  const contractorById = useMemo(() => Object.fromEntries(contractors.map((c) => [c.id, c])), [contractors]);

  // Only incidents with coords are mappable
  const mappable = useMemo(
    () => incidents.filter((i) => i.location_lat != null && i.location_lng != null),
    [incidents],
  );

  const active = useMemo(() => incidents.filter((i) => ['open', 'in_progress'].includes(i.status)), [incidents]);
  const counts = useMemo(() => ({
    open: incidents.filter((i) => i.status === 'open').length,
    in_progress: incidents.filter((i) => i.status === 'in_progress').length,
    contractors: contractors.filter((c) => c.active).length,
  }), [incidents, contractors]);

  const selected = useMemo(
    () => incidents.find((i) => i.id === selectedId) || active[0] || null,
    [incidents, selectedId, active],
  );

  // Selecting an incident from the right-rail / pin should clear the lookup focus.
  const selectIncident = useCallback((id) => {
    setLookupCoords(null);
    setSelectedId(id);
  }, []);

  const clearLookup = () => { setLookupCoords(null); setLookupAddress(''); };

  // Map centring — fresh lookup pin wins; otherwise selected incident; otherwise SA default.
  const focusLat = lookupCoords?.lat ?? selected?.location_lat ?? null;
  const focusLng = lookupCoords?.lng ?? selected?.location_lng ?? null;
  const focusZoom = lookupCoords ? 16 : (selected ? 14 : DEFAULT_ZOOM);

  // Address lookup — Nominatim (free, used elsewhere in the app).
  // Strategy: first try the user's exact query (countrycodes=za). If 0 results, retry with ", South Africa" appended.
  const runSearch = async (q) => {
    if (!q || q.trim().length < 4) return;
    setLookupBusy(true);
    const tryQuery = async (text) => {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(text)}&limit=1&countrycodes=za&addressdetails=1`,
      );
      return res.json();
    };
    try {
      let data = await tryQuery(q);
      if (!data || data.length === 0) {
        data = await tryQuery(`${q}, South Africa`);
      }
      if (data && data.length > 0) {
        const r = data[0];
        const lat = parseFloat(r.lat);
        const lng = parseFloat(r.lon);
        setLookupCoords({ lat, lng });
        setLookupAddress(r.display_name);
      } else {
        toast.error(`No South African results for "${q}"`);
      }
    } catch (e) { toast.error('Address lookup failed'); }
    finally { setLookupBusy(false); }
  };

  const handleAddressChange = (val) => {
    setLookupAddress(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(val), 700);
  };

  const handleCopyCoords = () => {
    if (!lookupCoords) return;
    const txt = `${lookupCoords.lat.toFixed(6)}, ${lookupCoords.lng.toFixed(6)}`;
    if (navigator.clipboard) navigator.clipboard.writeText(txt);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  // Place contractor markers near each active incident assignment (approx — contractors don't have GPS in the model yet)
  const contractorPins = useMemo(() => {
    const pins = [];
    incidents.forEach((inc) => {
      if (!['open', 'in_progress'].includes(inc.status)) return;
      if (inc.location_lat == null || inc.location_lng == null) return;
      (inc.assignments || []).forEach((a, idx) => {
        if (a.ended_at) return;
        const c = contractorById[a.contractor_id];
        if (!c) return;
        // Slight offset so they don't overlap incident pin
        const dx = (idx + 1) * 0.0035 * Math.cos((idx * 80) * Math.PI / 180);
        const dy = (idx + 1) * 0.0035 * Math.sin((idx * 80) * Math.PI / 180);
        const initials = (c.company_name || '').split(' ').map((n) => n[0]).join('').slice(0, 3).toUpperCase();
        pins.push({
          id: `${inc.id}-${a.id}`,
          lat: inc.location_lat + dy,
          lng: inc.location_lng + dx,
          name: c.company_name,
          incident_no: inc.incident_no,
          initials,
        });
      });
    });
    return pins;
  }, [incidents, contractorById]);

  if (loading) {
    return (
      <Layout title="Map View" subtitle="Live operations — incidents, contractors & location lookup">
        <div className="flex items-center justify-center py-20 text-slate-400 gap-2">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading operations map…
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Map View" subtitle="Live operations — incidents, contractors & location lookup">
      <div className="space-y-4 animate-fade-in" data-testid="map-view-page">
        {/* Top toolbar */}
        <Card className="bg-white border-slate-200">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2 pr-4 border-r border-slate-200">
                <div className="w-8 h-8 bg-[#0D5B69] rounded-md flex items-center justify-center">
                  <Radio className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Control Room</p>
                  <p className="text-sm font-semibold text-slate-900">Live Operations</p>
                </div>
              </div>

              <Badge className="bg-red-100 text-red-700 border-red-200 hover:bg-red-100 gap-1" data-testid="badge-open">
                <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse"></span>
                {counts.open} Open
              </Badge>
              <Badge className="bg-orange-100 text-orange-700 border-orange-200 hover:bg-orange-100" data-testid="badge-inprogress">
                {counts.in_progress} In Progress
              </Badge>
              <Badge className="bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-100" data-testid="badge-contractors">
                {counts.contractors} Contractors
              </Badge>

              <div className="flex items-center gap-2 text-xs ml-2" data-testid="live-indicator">
                <span className="relative flex h-2 w-2">
                  <span className={`${live ? 'animate-ping bg-[#80C030]' : 'bg-slate-300'} absolute inline-flex h-full w-full rounded-full opacity-75`} />
                  <span className={`${live ? 'bg-[#80C030]' : 'bg-slate-400'} relative inline-flex rounded-full h-2 w-2`} />
                </span>
                <span className="text-slate-500">{live ? 'Live' : 'Connecting…'}</span>
              </div>

              <div className="flex-1"></div>

              <Button variant="outline" size="sm" className="gap-1" onClick={load} data-testid="refresh-btn">
                <RefreshCw className="w-4 h-4" /> Refresh
              </Button>
              <Button className="bg-[#80C030] hover:bg-[#6FA528] text-white gap-1 font-semibold" onClick={() => navigate('/occurrence-book')} data-testid="new-incident-btn">
                <Siren className="w-4 h-4" /> Log Incident
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-12 gap-4">
          {/* Left: Location Lookup */}
          <div className="col-span-12 lg:col-span-3 space-y-4">
            <Card className="bg-white border-slate-200" data-testid="address-lookup-card">
              <CardHeader className="pb-3 border-b border-slate-100">
                <CardTitle className="font-heading text-base flex items-center gap-2 text-[#0D5B69]">
                  <Target className="w-4 h-4 text-[#80C030]" />
                  Location Lookup
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wide text-slate-600">Address (South Africa)</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="text"
                      value={lookupAddress}
                      onChange={(e) => handleAddressChange(e.target.value)}
                      placeholder="e.g. 15 Rivonia Rd, Sandton"
                      className="pl-9"
                      data-testid="address-input"
                    />
                    {lookupBusy ? (
                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-slate-400" />
                    ) : lookupAddress ? (
                      <button
                        type="button"
                        onClick={clearLookup}
                        className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded hover:bg-slate-100 flex items-center justify-center text-slate-400 text-lg leading-none"
                        data-testid="clear-lookup-btn"
                        aria-label="Clear lookup"
                      >
                        ×
                      </button>
                    ) : null}
                  </div>
                  <p className="text-[11px] text-slate-400 italic">Auto-search after you stop typing. Map flies to the result.</p>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wide text-slate-600">GPS Coordinates</Label>
                  <div className="flex items-stretch gap-2">
                    <div className="flex-1 relative">
                      <Crosshair className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        type="text"
                        readOnly
                        value={lookupCoords ? `${lookupCoords.lat.toFixed(6)}, ${lookupCoords.lng.toFixed(6)}` : ''}
                        placeholder="Search an address →"
                        className="pl-9 font-mono text-sm bg-slate-50"
                        data-testid="coords-input"
                      />
                    </div>
                    <Button
                      onClick={handleCopyCoords}
                      disabled={!lookupCoords}
                      className={`${copied ? 'bg-emerald-600 hover:bg-emerald-600' : 'bg-[#0D5B69] hover:bg-[#0A4B57]'} text-white px-3 transition-all`}
                      data-testid="copy-coords-btn"
                    >
                      {copied ? <><Check className="w-4 h-4 mr-1" /> Copied</> : <><Copy className="w-4 h-4 mr-1" /> Copy</>}
                    </Button>
                  </div>
                  <p className="text-[11px] text-slate-400 italic">Paste into WhatsApp / SMS / radio dispatch.</p>
                </div>
              </CardContent>
            </Card>

            {/* Available Units summary */}
            <Card className="bg-white border-slate-200" data-testid="contractors-summary-card">
              <CardHeader className="pb-3 border-b border-slate-100">
                <CardTitle className="font-heading text-base flex items-center justify-between text-[#0D5B69]">
                  <span>Contractors</span>
                  <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-xs">{counts.contractors}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0 max-h-60 overflow-y-auto">
                {contractors.filter((c) => c.active).slice(0, 8).map((c) => (
                  <div key={c.id} className="flex items-center gap-3 p-3 border-b border-slate-100 last:border-b-0 hover:bg-slate-50">
                    <div className="w-9 h-9 bg-[#0D5B69]/10 rounded-full flex items-center justify-center shrink-0">
                      <span className="text-[10px] font-bold text-[#0D5B69]">
                        {(c.company_name || '').split(' ').map((n) => n[0]).join('').slice(0, 3).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{c.company_name}</p>
                      <p className="text-[11px] text-slate-500 truncate">{c.area_of_operation || '—'}</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Center: Live Leaflet map */}
          <div className="col-span-12 lg:col-span-6">
            <Card className="bg-white border-slate-200 overflow-hidden h-full" data-testid="map-panel">
              <CardContent className="p-0 relative">
                <div className="relative w-full" style={{ height: 620 }}>
                  <MapContainer
                    center={focusLat != null ? [focusLat, focusLng] : SA_DEFAULT}
                    zoom={focusLat != null ? 13 : DEFAULT_ZOOM}
                    style={{ height: '100%', width: '100%' }}
                    scrollWheelZoom
                  >
                    <LayersControl position="topright">
                      <LayersControl.BaseLayer checked name="Streets">
                        <TileLayer
                          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                          url="https://{s}.tile.openstreetmap.org/{z}/{y}/{x}.png"
                        />
                      </LayersControl.BaseLayer>
                      <LayersControl.BaseLayer name="Satellite">
                        <TileLayer
                          attribution='Tiles &copy; <a href="https://www.esri.com/">Esri</a> &mdash; Source: Esri, Maxar, Earthstar Geographics'
                          url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                          maxZoom={19}
                        />
                      </LayersControl.BaseLayer>
                      <LayersControl.Overlay name="Place &amp; Road Labels">
                        <TileLayer
                          attribution='Reference &copy; Esri'
                          url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
                          maxZoom={19}
                        />
                      </LayersControl.Overlay>
                    </LayersControl>

                    {/* Incident markers */}
                    {mappable.map((inc) => {
                      const isSel = inc.id === selected?.id;
                      const isActive = ['open', 'in_progress'].includes(inc.status);
                      const sla = isActive ? computeSla(inc, serviceById) : null;
                      const slaTone = sla?.tone;
                      return (
                        <Marker
                          key={inc.id}
                          position={[inc.location_lat, inc.location_lng]}
                          icon={incidentIcon(inc.status, isActive && isSel, slaTone)}
                          eventHandlers={{ click: () => selectIncident(inc.id) }}
                        >
                          <Popup>
                            <div className="text-xs space-y-1">
                              <p className="font-bold text-slate-900 leading-tight">{customerById[inc.customer_id]?.company_name || '—'}</p>
                              <p className="font-semibold text-[#0D5B69]">{serviceById[inc.primary_service_id]?.name || '—'}</p>
                              <p className="text-slate-500">{inc.location_address || '—'}</p>
                              {sla && sla.tone !== 'none' && (
                                <div className="flex items-center gap-1.5 pt-0.5">
                                  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold ${SLA_TONE_META[sla.tone].bg} ${SLA_TONE_META[sla.tone].text}`}>
                                    <span className={`w-1 h-1 rounded-full ${SLA_TONE_META[sla.tone].dot}`} />
                                    SLA · {fmtSlaShort(sla)}
                                  </span>
                                </div>
                              )}
                              <div className="flex items-center justify-between gap-2 pt-0.5">
                                <Badge className="text-[10px]" style={{ background: STATUS_COLOR[inc.status] + '22', color: STATUS_COLOR[inc.status], border: `1px solid ${STATUS_COLOR[inc.status]}33` }}>
                                  {STATUS_LABEL[inc.status] || inc.status}
                                </Badge>
                                <span className="font-mono text-[10px] text-slate-400">{inc.incident_no}</span>
                              </div>
                            </div>
                          </Popup>
                        </Marker>
                      );
                    })}

                    {/* Contractor pins (offset from incident location) */}
                    {contractorPins.map((p) => (
                      <Marker key={p.id} position={[p.lat, p.lng]} icon={contractorIcon(p.initials)}>
                        <Popup>
                          <div className="text-xs">
                            <p className="font-semibold">{p.name}</p>
                            <p className="text-slate-500">Assigned to {p.incident_no}</p>
                          </div>
                        </Popup>
                      </Marker>
                    ))}

                    {/* Lookup pin */}
                    {lookupCoords && (
                      <Marker position={[lookupCoords.lat, lookupCoords.lng]}>
                        <Popup>
                          <div className="text-xs">
                            <p className="font-semibold">Address Lookup</p>
                            <p className="text-slate-500">{lookupAddress}</p>
                          </div>
                        </Popup>
                      </Marker>
                    )}

                    <Recenter lat={focusLat} lng={focusLng} zoom={focusZoom} />
                  </MapContainer>

                  {/* Legend */}
                  <div className="absolute bottom-3 left-3 z-[400] bg-white/95 backdrop-blur-sm rounded-lg shadow-lg border border-slate-200 p-3">
                    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Legend</p>
                    <div className="space-y-1.5">
                      {['open', 'in_progress', 'closed', 'cancelled'].map((s) => (
                        <div key={s} className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full border border-white shadow" style={{ background: STATUS_COLOR[s] }}></div>
                          <span className="text-xs text-slate-700">{STATUS_LABEL[s]}</span>
                        </div>
                      ))}
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full border border-white shadow bg-[#0D5B69]"></div>
                        <span className="text-xs text-slate-700">Contractor</span>
                      </div>
                    </div>
                  </div>

                  {/* Map count badge */}
                  <div className="absolute top-3 left-14 z-[400] bg-white/95 backdrop-blur-sm rounded-md shadow-sm border border-slate-200 px-2 py-1 text-xs text-slate-600" data-testid="mappable-count">
                    Showing <span className="font-semibold">{mappable.length}</span> of {incidents.length} incidents (with coordinates)
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right: Selected incident detail + Active list */}
          <div className="col-span-12 lg:col-span-3 space-y-4">
            {selected ? (
              <Card className="bg-white border-slate-200" data-testid="selected-incident-card">
                <CardHeader className="pb-3 border-b border-slate-100" style={{ background: STATUS_COLOR[selected.status] + '14' }}>
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-heading text-base flex items-center gap-2 text-[#0D5B69]">
                      <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: STATUS_COLOR[selected.status] }}></span>
                      Selected Incident
                    </CardTitle>
                    <Badge style={{ background: STATUS_COLOR[selected.status] + '22', color: STATUS_COLOR[selected.status], border: `1px solid ${STATUS_COLOR[selected.status]}33` }}>
                      {STATUS_LABEL[selected.status] || selected.status}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="p-4 space-y-3">
                  <DetailRow label="Customer" value={<span className="font-bold text-slate-900">{customerById[selected.customer_id]?.company_name || '—'}</span>} />
                  <DetailRow label="Service Type" value={<span className="font-semibold text-[#0D5B69]">{serviceById[selected.primary_service_id]?.name || '—'}</span>} />
                  <DetailRow label="Incident ID" value={<span className="font-mono text-xs text-slate-500">{selected.incident_no}</span>} />
                  <div>
                    <p className="text-[10px] text-slate-400 font-semibold tracking-wider">LOCATION</p>
                    <p className="text-sm text-slate-700 flex items-start gap-1">
                      <MapPin className="w-3.5 h-3.5 mt-0.5 text-slate-400 shrink-0" />
                      {selected.location_address || '—'}
                    </p>
                    {selected.location_lat != null && (
                      <p className="text-[11px] font-mono text-slate-500 mt-1 pl-5">
                        {selected.location_lat.toFixed(6)}, {selected.location_lng.toFixed(6)}
                      </p>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-100">
                    <div>
                      <p className="text-[10px] text-slate-400 font-semibold tracking-wider">CONTRACTORS</p>
                      <p className="text-sm text-slate-700 flex items-center gap-1">
                        <User className="w-3 h-3 text-slate-400" />
                        {(selected.assignments || []).length || 'Unassigned'}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-400 font-semibold tracking-wider">OPENED</p>
                      <p className="text-sm text-slate-700 flex items-center gap-1">
                        <Clock className="w-3 h-3 text-slate-400" />
                        {selected.opened_at?.slice(11, 16) || '—'}
                      </p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    className="w-full bg-[#80C030] hover:bg-[#6FA528] text-white gap-1 font-semibold"
                    onClick={() => navigate('/occurrence-book', { state: { openIncidentId: selected.id, defaultTab: 'notes' } })}
                    data-testid="open-incident-btn"
                  >
                    Open in Occurrence Book
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-white border-slate-200">
                <CardContent className="p-6 text-center text-sm text-slate-400">
                  No incident selected. Click a pin on the map.
                </CardContent>
              </Card>
            )}

            {/* Active incidents list */}
            <Card className="bg-white border-slate-200" data-testid="active-incidents-list">
              <CardHeader className="pb-3 border-b border-slate-100">
                <CardTitle className="font-heading text-base text-[#0D5B69] flex items-center justify-between">
                  <span>Active Incidents</span>
                  <Badge className="bg-red-100 text-red-700 border-red-200">{active.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0 max-h-80 overflow-y-auto">
                {active.length === 0 ? (
                  <p className="text-center text-slate-400 py-8 text-sm">No active incidents.</p>
                ) : active.map((inc, idx) => {
                  const sla = computeSla(inc, serviceById);
                  const tone = sla && sla.tone !== 'none' ? SLA_TONE_META[sla.tone] : null;
                  return (
                  <button
                    key={inc.id}
                    onClick={() => selectIncident(inc.id)}
                    className={`w-full text-left p-3 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition-colors ${selected?.id === inc.id ? 'bg-[#80C030]/5 border-l-2 border-l-[#80C030]' : ''}`}
                    data-testid={`active-incident-${idx}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-slate-900 truncate">{customerById[inc.customer_id]?.company_name || '—'}</p>
                        <p className="text-xs text-[#0D5B69] font-semibold truncate">{serviceById[inc.primary_service_id]?.name || '—'}</p>
                        <p className="text-[11px] text-slate-500 truncate flex items-center gap-1">
                          <MapPin className="w-3 h-3 shrink-0" />
                          {inc.location_address || '—'}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <p className="text-[10px] text-slate-400 font-mono">{inc.incident_no}</p>
                          {tone && (
                            <span
                              data-sla-tone={sla.tone}
                              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold ${tone.bg} ${tone.text} ${sla.tone === 'breach' ? 'animate-pulse' : ''}`}
                            >
                              <span className={`w-1 h-1 rounded-full ${tone.dot}`} />
                              {fmtSlaShort(sla)}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="w-2 h-2 rounded-full mt-1 shrink-0 animate-pulse" style={{ background: STATUS_COLOR[inc.status] }}></div>
                    </div>
                  </button>
                );})}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};


const DetailRow = ({ label, value }) => (
  <div>
    <p className="text-[10px] text-slate-400 font-semibold tracking-wider">{label?.toString().toUpperCase()}</p>
    <p className="text-sm text-slate-700">{value}</p>
  </div>
);
